import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import { Button } from '@ooh/ui/components/button';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { MapView } from '@ooh/ui/components/map-view';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import type { PoiCategory } from '@ooh/contracts';
import { useCreatePoi } from '../api/mutations';
import type { WithCallWithAuth } from '../api/queries';
import { getCategoryLabel } from '../utils/category-label';
import { encodeCategoryKey, decodeCategoryKey } from '../utils/poi-constants';

const WARSAW_CENTER: [number, number] = [52.23, 21.01];
const MAP_DEFAULT_ZOOM = 10;

interface CreatePoiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: PoiCategory[];
  auth?: WithCallWithAuth;
}

interface FormErrors {
  name?: string;
  category?: string;
  lat?: string;
  lng?: string;
}

function isValidLat(value: string): boolean {
  const n = parseFloat(value);
  return value !== '' && !isNaN(n) && n >= -90 && n <= 90;
}

function isValidLng(value: string): boolean {
  const n = parseFloat(value);
  return value !== '' && !isNaN(n) && n >= -180 && n <= 180;
}

export function CreatePoiDialog({ open, onOpenChange, categories, auth }: CreatePoiDialogProps) {
  const { t } = useTranslation('map');
  const { mutate, isPending, isError } = useCreatePoi(auth);

  const [name, setName] = React.useState('');
  const [categoryKey, setCategoryKey] = React.useState('');
  const [lat, setLat] = React.useState('');
  const [lng, setLng] = React.useState('');
  const [internalOnly, setInternalOnly] = React.useState(false);
  const [errors, setErrors] = React.useState<FormErrors>({});

  const resetForm = () => {
    setName('');
    setCategoryKey('');
    setLat('');
    setLng('');
    setInternalOnly(false);
    setErrors({});
  };

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (name.trim() === '') errs.name = t('admin.pois.form.nameRequired');
    if (categoryKey === '') errs.category = t('admin.pois.form.categoryRequired');
    if (!isValidLat(lat)) errs.lat = t('admin.pois.form.latError');
    if (!isValidLng(lng)) errs.lng = t('admin.pois.form.lngError');
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    const [osmKey, osmValue] = decodeCategoryKey(categoryKey);
    mutate(
      {
        name,
        osm_key: osmKey ?? '',
        osm_value: osmValue ?? '',
        location: { lat: parseFloat(lat), lng: parseFloat(lng) },
        internal_only: internalOnly,
      },
      {
        onSuccess: () => {
          toast.success(t('admin.pois.toast.createSuccess'));
          resetForm();
          onOpenChange(false);
        },
      },
    );
  };

  if (!open) return null;

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const categoryOptions = categories.map((cat) => ({
    value: encodeCategoryKey(cat.osm_key, cat.osm_value),
    label: getCategoryLabel(cat, t),
  }));

  return (
    <ModalDialog
      testId="create-poi-dialog"
      header={<h2 className="text-[17px] font-semibold">{t('admin.pois.addButton')}</h2>}
      onClose={handleClose}
      closeLabel={t('admin.pois.deleteDialog.cancel')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            {t('admin.pois.deleteDialog.cancel')}
          </Button>
          <Button
            type="submit"
            // eslint-disable-next-line i18next/no-literal-string
            form="create-poi-form"
            size="sm"
            disabled={isPending}
            data-testid="poi-form-submit"
          >
            {isPending ? t('admin.pois.form.submittingLabel') : t('admin.pois.form.submitLabel')}
          </Button>
        </>
      }
    >
      <form id="create-poi-form" onSubmit={handleSubmit} noValidate>
        {/* Name */}
        <div className="mb-3">
          <Label htmlFor="poi-name">{t('admin.pois.form.nameLabel')}</Label>
          <Input
            id="poi-name"
            data-testid="poi-form-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            placeholder={t('admin.pois.form.namePlaceholder')}
            className="mt-1"
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name ? (
            <p className="text-[11px] text-destructive mt-0.5" data-testid="poi-form-name-error">
              {errors.name}
            </p>
          ) : null}
        </div>

        {/* Category */}
        <div className="mb-3">
          <Label htmlFor="poi-category">{t('admin.pois.form.categoryLabel')}</Label>
          <div className="mt-1">
            <SearchableSelectField
              id="poi-category"
              options={categoryOptions}
              value={categoryKey}
              onChange={(value) => {
                setCategoryKey(value);
                if (errors.category) setErrors((prev) => ({ ...prev, category: undefined }));
              }}
              placeholder={t('admin.pois.form.categoryPlaceholder')}
              error={Boolean(errors.category)}
              data-testid="poi-form-category"
            />
          </div>
          {errors.category ? (
            <p
              className="text-[11px] text-destructive mt-0.5"
              data-testid="poi-form-category-error"
            >
              {errors.category}
            </p>
          ) : null}
        </div>

        {/* Map picker */}
        <div
          className="mb-3 h-48 rounded-md overflow-hidden border border-border"
          data-testid="poi-form-map"
        >
          <MapView
            pins={[]}
            initialCenter={WARSAW_CENTER}
            initialZoom={MAP_DEFAULT_ZOOM}
            onMapClick={(clickLat, clickLng) => {
              setLat(String(clickLat));
              setLng(String(clickLng));
              setErrors((prev) => ({ ...prev, lat: undefined, lng: undefined }));
            }}
            markerPosition={
              isValidLat(lat) && isValidLng(lng)
                ? { lat: parseFloat(lat), lng: parseFloat(lng) }
                : null
            }
          />
        </div>

        {/* Lat / Lng */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label htmlFor="poi-lat">{t('admin.pois.form.latLabel')}</Label>
            <Input
              id="poi-lat"
              data-testid="poi-form-lat"
              type="number"
              // eslint-disable-next-line i18next/no-literal-string
              step="any"
              value={lat}
              onChange={(e) => {
                setLat(e.target.value);
                if (errors.lat) setErrors((prev) => ({ ...prev, lat: undefined }));
              }}
              className="mt-1"
              aria-invalid={Boolean(errors.lat)}
            />
            {errors.lat ? (
              <p className="text-[11px] text-destructive mt-0.5" data-testid="poi-form-lat-error">
                {errors.lat}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="poi-lng">{t('admin.pois.form.lngLabel')}</Label>
            <Input
              id="poi-lng"
              data-testid="poi-form-lng"
              type="number"
              // eslint-disable-next-line i18next/no-literal-string
              step="any"
              value={lng}
              onChange={(e) => {
                setLng(e.target.value);
                if (errors.lng) setErrors((prev) => ({ ...prev, lng: undefined }));
              }}
              className="mt-1"
              aria-invalid={Boolean(errors.lng)}
            />
            {errors.lng ? (
              <p className="text-[11px] text-destructive mt-0.5" data-testid="poi-form-lng-error">
                {errors.lng}
              </p>
            ) : null}
          </div>
        </div>

        {/* Internal only */}
        <div className="flex items-center gap-2 mb-4">
          <Checkbox
            id="poi-internal-only"
            data-testid="poi-form-internal-only"
            checked={internalOnly}
            onCheckedChange={(v) => setInternalOnly(v === true)}
          />
          <Label htmlFor="poi-internal-only" className="cursor-pointer">
            {t('admin.pois.form.internalOnlyLabel')}
          </Label>
        </div>

        {/* API error */}
        {isError ? (
          <p className="text-[12px] text-destructive mb-3" data-testid="poi-form-api-error">
            {t('admin.pois.form.apiError')}
          </p>
        ) : null}
      </form>
    </ModalDialog>
  );
}
